/**
 * Phase 18 — Invoice Financial Calculations & Precision Utilities
 * Safe numeric math, line totals, tax reconciliation, and overdue date analytics.
 */

import { PaymentReadinessStatus } from '@/types/domain/invoice-management';

/**
 * Rounds any financial amount to 2 decimal places safely avoiding floating point artifacts.
 */
export function roundCurrency(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Calculates item-level financial figures from quantity, unit price, tax percentage, and discount.
 */
export function calculateLineTotal(
  quantity: number,
  unitPrice: number,
  taxRatePercent: number = 0,
  discount: number = 0
): {
  lineSubtotal: number;
  discountAmount: number;
  taxableAmount: number;
  taxAmount: number;
  lineTotal: number;
} {
  const safeQty = Math.max(0, quantity);
  const safePrice = Math.max(0, unitPrice);
  const safeTaxRate = Math.max(0, taxRatePercent);
  const safeDiscount = Math.max(0, discount);

  const rawSubtotal = safeQty * safePrice;
  const lineSubtotal = roundCurrency(rawSubtotal);
  const discountAmount = roundCurrency(safeDiscount);
  const taxableAmount = Math.max(0, roundCurrency(lineSubtotal - discountAmount));
  const taxAmount = roundCurrency(taxableAmount * (safeTaxRate / 100));
  const lineTotal = roundCurrency(taxableAmount + taxAmount);

  return {
    lineSubtotal,
    discountAmount,
    taxableAmount,
    taxAmount,
    lineTotal,
  };
}

/**
 * Aggregates all line item figures into commercial summary totals.
 */
export function calculateInvoiceTotals(
  items: Array<{
    quantity: number;
    unitPrice: number;
    taxRate: number;
    discount?: number;
  }>,
  shippingAmount: number = 0,
  otherCharges: number = 0,
  overallDiscountAmount: number = 0
): {
  subtotal: number;
  totalTax: number;
  totalDiscounts: number;
  shipping: number;
  otherCharges: number;
  grandTotal: number;
} {
  let subtotal = 0;
  let totalTax = 0;
  let lineDiscounts = 0;

  for (const item of items) {
    const calc = calculateLineTotal(item.quantity, item.unitPrice, item.taxRate, item.discount || 0);
    subtotal += calc.lineSubtotal;
    totalTax += calc.taxAmount;
    lineDiscounts += calc.discountAmount;
  }

  const safeShipping = Math.max(0, shippingAmount);
  const safeOther = Math.max(0, otherCharges);
  const safeOverallDiscount = Math.max(0, overallDiscountAmount);

  subtotal = roundCurrency(subtotal);
  totalTax = roundCurrency(totalTax);
  const totalDiscounts = roundCurrency(lineDiscounts + safeOverallDiscount);
  const shipping = roundCurrency(safeShipping);
  const other = roundCurrency(safeOther);

  const grandTotal = roundCurrency(
    Math.max(0, subtotal + totalTax + shipping + other - totalDiscounts)
  );

  return {
    subtotal,
    totalTax,
    totalDiscounts,
    shipping,
    otherCharges: other,
    grandTotal,
  };
}

/**
 * Validates whether the declared grand total reconciles with subtotal + tax + charges - discounts.
 */
export function reconcileInvoiceTotals(
  subtotal: number,
  taxAmount: number,
  shippingAmount: number,
  otherCharges: number,
  discountAmount: number,
  declaredGrandTotal: number,
  tolerance: number = 0.02
): { isReconciled: boolean; expectedTotal: number; difference: number } {
  const expectedTotal = roundCurrency(
    subtotal + taxAmount + shippingAmount + otherCharges - discountAmount
  );
  const diff = roundCurrency(Math.abs(expectedTotal - declaredGrandTotal));

  return {
    isReconciled: diff <= tolerance,
    expectedTotal,
    difference: diff,
  };
}

/**
 * Computes calendar overdue metrics based on target due date and settlement state.
 */
export function calculateDaysOverdue(
  dueDate: string,
  paymentStatus: PaymentReadinessStatus
): { isOverdue: boolean; daysOverdue: number } {
  if (paymentStatus === 'PAID' || paymentStatus === 'CANCELLED' || !dueDate) {
    return { isOverdue: false, daysOverdue: 0 };
  }

  const targetTime = new Date(dueDate).getTime();
  const now = Date.now();
  const diffMs = now - targetTime;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) {
    return { isOverdue: true, daysOverdue: diffDays };
  }

  return { isOverdue: false, daysOverdue: 0 };
}

/**
 * Validates invoice date sequence constraints.
 */
export function detectDateExceptions(
  invoiceDate: string,
  receivedDate: string,
  dueDate: string
): { hasDateException: boolean; exceptionReason?: string } {
  if (!invoiceDate) {
    return { hasDateException: true, exceptionReason: 'Invoice date is missing.' };
  }

  const invTime = new Date(invoiceDate).getTime();
  const now = Date.now();

  // Future dated invoice check (> 24 hours ahead)
  if (invTime > now + 86400000) {
    return {
      hasDateException: true,
      exceptionReason: 'Future-dated invoice detected. Invoice date is ahead of current date.',
    };
  }

  if (dueDate) {
    const dueTime = new Date(dueDate).getTime();
    if (dueTime < invTime) {
      return {
        hasDateException: true,
        exceptionReason: 'Due date cannot precede invoice creation date.',
      };
    }
  }

  if (receivedDate) {
    const recTime = new Date(receivedDate).getTime();
    // Received date earlier than invoice date by more than 1 day
    if (recTime < invTime - 86400000) {
      return {
        hasDateException: true,
        exceptionReason: 'Physical dock receipt date precedes invoice date.',
      };
    }
  }

  return { hasDateException: false };
}
